/**
  * 入力チェック
  * (C)2024 Chubu Electric Power Grid Co.,Inc. All Rights Reserved.
  * version 1.00 NEC 2024/12/31 初版
  */
import {format} from 'date-fns';
import {utcToZonedTime} from 'date-fns-tz';
import {formatRowIndex} from './common.js';
import {isEmpty} from '@grapecity/wijmo';

/**
   * 必須チェック
   * @param {string} label 項目名
   * @param {string} value チェック対象
   * @param {*} rowIndex 行番号
   * @param {boolean} isPk true:PK, false:PK以外
   * @return {object} 結果
   */
export function requiredCheck(label, value, rowIndex=' ', isPk=false) {
  if (value === undefined || value === null ||
      String(value).length === 0 || String(value) === 'false') {
    let _label = label;
    let result = errorResult('TKSCS00005E', [_label, formatRowIndex(rowIndex)]);
    result.message['isPk'] = isPk;
    return result;
  }
  return {error: false, message: null};
};

/**
   * ファイル必須チェック
   * @param {string} label 項目名
   * @param {string} value チェック対象
   * @param {*} rowIndex 行番号
   * @return {object} 結果
   */
export function requiredFileCheck(label, value, rowIndex=' ') {
  if (!value) {
    return errorResult('BFY000018E', [label, formatRowIndex(rowIndex)]);
  }
  return {error: false, message: null};
};

/**
   * 桁数チェック
   * @param {string} label 項目名
   * @param {string} value チェック対象
   * @param {number} max 最大桁数
   * @param {number} rowIndex 行番号
   * @return {object} 結果
   */
export function lengthCheck(label, value, max, rowIndex=' ') {
  if (value !== undefined && value !== null && value.length > max) {
    let _label = label;
    return errorResult('TKSCS00009E', [_label, formatRowIndex(rowIndex)]);
  }
  return {error: false, message: null};
};

/**
   * 期限日チェック
   * @param {string} label 項目名
   * @param {string} value チェック対象
   * @param {number} rowIndex 行番号
   * @return {object} 結果
   */
const timeZone = 'Asia/Tokyo';
const zonedDate = utcToZonedTime(new Date(), timeZone);
const today = format(zonedDate, 'yyyy-MM-dd');
export function limitCheck(label, value, rowIndex=0) {
  if (value && value < today) {
    let _label = label;
    return errorResult('TKSCS00005E', [_label]);
  }
  return {error: false, message: null};
};

/**
   * 範囲内チェック
   * @param {string} label 項目名
   * @param {string} value チェック対象
   * @param {number} min 最小値
   * @param {number} max 最大値
   * @param {number} rowIndex 行番号
   * @return {object} 結果
   */
export function numberRangeCheck(label, value, min, max, rowIndex=' ') {
  if (value != null && min != null && max != null &&
    (value < min || value > max)) {
    let _label = label;
    return errorResult('BFY000026E', [_label, min + ' ～ ' + max, formatRowIndex(rowIndex)]);
  }
  return {error: false, message: null};
};

/**
   * 範囲内（警告）チェック
   * @param {string} label 項目名
   * @param {string} value チェック対象
   * @param {number} min 最小値
   * @param {number} max 最大値
   * @param {number} rowIndex 行番号
   * @return {object} 結果
   */
export function numberRangeWarningCheck(label, value, min, max, rowIndex=' ') {
  if (!value || (value < min || value > max)) {
    let _label = label;
    return errorResult('BFY000178W', [_label, min + ' ～ ' + max]);
  }
  return {error: false, message: null};
};

/**
   * 日付整合性チェック
   * @param {string} fromLabel 項目名
   * @param {string} fromDate チェック対象
   * @param {string} toLabel 項目名
   * @param {string} toDate チェック対象
   * @return {object} 結果
   */
export function dateFromToCheck(fromLabel, fromDate, toLabel, toDate) {
  if (fromDate && toDate && fromDate > toDate) {
    return errorResult('TKSCS00010E', [fromLabel, toLabel]);
  }
  return {error: false, message: null};
};


/**
 * 日付妥当性チェック
 * @param {string} label 項目名
 * @param {string} value チェック対象
 * @return {object} 結果
 */
export function dateCheck(label, value) {
  const dateRegex = /^\d{4}\/\d{2}\/\d{2}$/;
  const dateRegex2 = /^\d{4}-\d{2}-\d{2}$/;
  if (!isEmpty(value) && !(dateRegex.test(value) || dateRegex2.test(value))) {
    return errorResult('TKSCS00007E', [label]);
  }
  return {error: false, message: null};
};

/**
 * 時刻妥当性チェック
 * @param {string} label 項目名
 * @param {string} value チェック対象
 * @return {object} 結果
 */
export function timeCheck(label, value) {
  const timeRegex = /^\d{2}:\d{2}$/;
  if (!isEmpty(value) && !timeRegex.test(value)) {
    return errorResult('TKSCS00008E', [label]);
  }
  return {error: false, message: null};
};

/**
 * メールアドレスチェック
 * @param {string} label 項目名
 * @param {string} value チェック対象
 * @return {object} 結果
 */
export function mailCheck(label, value) {
  const mailRegex = /^[a-z\d][\w.-]*@[\w.-]+\.[a-z\d]+$/i;
  if (!isEmpty(value) && !mailRegex.test(value)) {
    return errorResult('TKSCS00016E', [label]);
  }
  return {error: false, message: null};
};


/**
  * 年度の妥当性チェック
  * @param {string} label 項目名
  * @param {string} value チェック対象
  * @param {number} rowIndex 行番号
  * @return {object} 結果
  */
export function yearIntegerCheck(label, value, rowIndex=' ') {
  const yearRegex = /^\d{4}$/;
  if (!isEmpty(value) && !yearRegex.test(value)) {
    return errorResult('TKSCS00029E', [label, formatRowIndex(rowIndex)]);
  }
  return {error: false, message: null};
};

/**
   * 新規代行申込入力確認
   * @param {boolean} daiko 申込代行
   * @param {boolean} zesei 是正申込
   * @return {object} 結果
   */
export function daikoZeseiCheck(daiko, zesei) {
  if (!daiko && !zesei) {
    return errorResult('BFA000157W');
  }
  return {error: false, message: null};
};

/**
 * メッセージ種別存在チェック
 * @param {string} kind メッセージ種別(E,W,I,C,S)
 * @param {array} messages メッセージリスト
 * @return {boolean} true: メッセージあり、false:メッセージなし
 */
export function hasMsg(kind, messages) {
  if (!messages) {
    return false;
  }
  const result = messages.filter((data) =>
    data.message?.id &&
    data.message.id[data.message.id.length - 1] == kind);
  return result.length > 0;
}

/**
 * エラーメッセージを抽出する
 * @param {array} msgIds メッセージIDリスト
 * @param {array} messages メッセージリスト
 * @return {array} エラーメッセージ
 */
export function filterErrorMessage(msgIds, messages) {
  if (!msgIds || !messages) {
    return [];
  }
  let pkMsgs = [];
  let otherMsgIds = [];
  for (const msgId of msgIds) {
    if (msgId == 'TKSCS00005E') {
      // 処理中断の判定に使うため、'TKSCS00005E'だけPKフラグが立っていたらエラーメッセージを抽出する
      pkMsgs = messages.filter((message) =>
        message.id && ['TKSCS00005E'].includes(message.id) && message.isPk);
    } else {
      otherMsgIds.push(msgId);
    }
  }
  const otherMsgs = messages.filter((message) =>
    message.id && otherMsgIds.includes(message.id));
  return pkMsgs.concat(otherMsgs);
}

/**
 * メッセージ取得
 * @param {String} msgId メッセージID
 * @param {String[]} labels パラメータ
 * @return {*} メッセージ結果
 */
export function errorResult(msgId, labels=null) {
  let result = {
    error: true,
    message: {
      id: msgId,
    }};
  if (labels) {
    result.message.values = labels;
  }
  return result;
};
